import * as Yup from 'yup'
import { getUrlDomain } from '../../../../../helpers/url'
import {
  AD_HEADLINE,
  CAROUSEL_ITEMS,
  CAROUSEL_ITEMS_LENGTH,
  CLICK_THROUGH_LINK,
  CUSTOMIZED_MEDIA_OPTION,
  CUSTOMIZED_STORY_FILE,
  EXISTING_POST,
  FORMAT_CAROUSEL,
  FORMAT_EXISTING_POST,
  FORMAT_OPTION,
  FORMAT_SINGLE,
  FORMAT_STORY,
  HEADLINE_ENTER,
  HEADLINE_OPTION,
  IMAGE,
  IMAGE_FORMATS,
  IMAGE_MAX_SIZE,
  IMAGE_MIN_HEIGHT,
  IMAGE_MIN_WIDTH,
  LEAD_GEN_FORM,
  IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY,
  MEDIA_MAX_RATIO_16_9,
  MEDIA_MAX_RATIO_1_91,
  MEDIA_MIN_RATIO_1,
  MEDIA_MIN_RATIO_9_16,
  NAME,
  PRIMARY_TEXT_LIST,
  STORIES_ITEMS,
  STORY_MEDIA_MIN_WIDTH,
  STORY_VIDEO_MAX_DURATION,
  STORY_VIDEO_MAX_SIZE,
  TEXT_ENTER,
  TEXT_OPTION,
  UPLOAD_CUSTOMIZED_MEDIA,
  VIDEO,
  VIDEO_FORMATS,
  VIDEO_MAX_DURATION,
  VIDEO_MAX_SIZE,
  VIDEO_MIN_DURATION,
  VIDEO_MIN_HEIGHT,
  VIDEO_MIN_WIDTH,
  MEDIA_MIN_RATIO_4_5
} from '../../fields'
import { mediaItemSchema, primaryTextItemShape } from '../../validation'
import {
  FILE_TYPE,
  FILE_URL,
  IMAGE_HASH,
  MEDIA_DURATION,
  MEDIA_FORMAT,
  MEDIA_HEIGHT,
  MEDIA_RATIO,
  MEDIA_SIZE,
  MEDIA_TYPE,
  MEDIA_WIDTH
} from '../../../../ReusableFormFields/AdFileUpload/fields'
import { LEAD_GENERATION } from '../../../../../constants/campaigns'

export const storiesItemShape = Yup.object({
  // FILE
  [FILE_URL]: Yup.string().required('Please select Image or Video file'),
  [CLICK_THROUGH_LINK]: Yup.string()
    .test('link', 'Please enter a valid URL, including the https://', link => getUrlDomain(link))
    .max(1024, 'There is a maximum limit of 1024 characters')
    .required('This field should not be empty')
})

const validateSingleMediaRatio = Yup.number().when(MEDIA_TYPE, {
  // single
  // video: 16:9 to 9:16
  // image: 1.91:1 to 4:5
  is: IMAGE,
  then: () =>
    Yup.number()
      .min(MEDIA_MIN_RATIO_4_5, 'Image ratio should be from 1.91:1 to 4:5')
      .max(MEDIA_MAX_RATIO_1_91, 'Image ratio should be from 1.91:1 to 4:5'),
  otherwise: () =>
    Yup.number().when(MEDIA_TYPE, {
      is: VIDEO,
      then: () =>
        Yup.number()
          .min(MEDIA_MIN_RATIO_9_16, 'Video ratio should be from 16:9 to 9:16')
          .max(MEDIA_MAX_RATIO_16_9, 'Video ratio should be from 16:9 to 9:16')
    })
})

const validateCarouselMediaRatio = Yup.number().when(MEDIA_TYPE, {
  // carousel
  // video: 1.91:1 to 1:1
  // image: 1.91:1 to 1:1
  is: IMAGE,
  then: () =>
    Yup.number()
      .min(MEDIA_MIN_RATIO_1, 'Image ratio should be from 1.91:1 to 1:1')
      .max(MEDIA_MAX_RATIO_1_91, 'Image ratio should be from 1.91:1 to 1:1'),
  otherwise: () =>
    Yup.number().when(MEDIA_TYPE, {
      is: VIDEO,
      then: () =>
        Yup.number()
          .min(MEDIA_MIN_RATIO_1, 'Video ratio should be from 1.91:1 to 1:1')
          .max(MEDIA_MAX_RATIO_1_91, 'Video ratio should be from 1.91:1 to 1:1')
    })
})

const validateStoryMediaRatio = Yup.number()
  // story
  // video and image: 16:9 to 9:16
  .min(MEDIA_MIN_RATIO_9_16, 'Image ratio should be 9:16')
  .max(MEDIA_MIN_RATIO_9_16, 'Image ratio should be 9:16')

const validateFeedAndStoryUniqueFileHash = Yup.string().test(
  'unique-file-hash',
  "The same file can't be used for both feed and stories/reels placements, please choose a different file or do not customise the stories/reels placement",
  function (value) {
    const { parent } = this
    const customizedFileHash = parent?.[CUSTOMIZED_STORY_FILE]?.[IMAGE_HASH]

    // Only validate if both files have hashes (are uploaded)
    if (!value || !customizedFileHash) {
      return true
    }

    // Check if the same file hash is used for both main media and customized story
    return value !== customizedFileHash
  }
)

export const getValidationSchema = (formatOption, lineItem) => {
  if (formatOption === FORMAT_EXISTING_POST) {
    return Yup.object({
      [NAME]: Yup.string().max(400, 'Name should have maximum 400 characters').required('Name Required'),
      [EXISTING_POST]: Yup.string().required('Please select post'),
      [CLICK_THROUGH_LINK]: Yup.string()
        .test('link', 'Please enter a valid URL, including the https://', link => getUrlDomain(link))
        .max(1024, 'There is a maximum limit of 1024 characters')
        .required('This field should not be empty')
    })
  }
  return Yup.object({
    // NAME
    [NAME]: Yup.string().max(400, 'Name should have maximum 400 characters').required('Name Required'),
    // TEXT
    [PRIMARY_TEXT_LIST]: Yup.array().when(FORMAT_OPTION, {
      is: val => val !== FORMAT_STORY,
      then: () =>
        Yup.array().when(TEXT_OPTION, {
          is: TEXT_ENTER,
          then: () => Yup.array().of(primaryTextItemShape)
        })
    }), // FILE
    [FILE_URL]: Yup.string().when(FORMAT_OPTION, {
      is: FORMAT_SINGLE,
      then: () => Yup.string().required('Please select Image or Video file')
    }),
    [IMAGE_HASH]: Yup.string().when(FORMAT_OPTION, {
      is: FORMAT_SINGLE,
      then: () => validateFeedAndStoryUniqueFileHash
    }),
    // MEDIA
    [MEDIA_FORMAT]: Yup.string().when([MEDIA_TYPE, FILE_TYPE], {
      // check the format only for loaded files from device
      is: (MEDIA_TYPE, FILE_TYPE) => MEDIA_TYPE && FILE_TYPE && MEDIA_TYPE === IMAGE,
      then: () =>
        Yup.string().test(MEDIA_FORMAT, 'Allowed image types are JPG, JPEG, PNG', (value, { parent }) => {
          return IMAGE_FORMATS.includes(parent[MEDIA_FORMAT])
        }),
      otherwise: () =>
        Yup.string().when([MEDIA_TYPE, FILE_TYPE], {
          is: (MEDIA_TYPE, FILE_TYPE) => MEDIA_TYPE && FILE_TYPE && MEDIA_TYPE === VIDEO,
          then: () =>
            Yup.string().test(MEDIA_FORMAT, 'Allowed video types are MP4, MOV or GIF', (value, { parent }) => {
              return VIDEO_FORMATS.includes(parent[MEDIA_FORMAT])
            })
        })
    }),
    [MEDIA_SIZE]: Yup.number().when(MEDIA_TYPE, {
      is: IMAGE,
      then: () => Yup.number().max(IMAGE_MAX_SIZE, 'The maximum supported image file size is 30 MB'),
      otherwise: () =>
        Yup.number().when(MEDIA_TYPE, {
          is: VIDEO,
          then: () =>
            Yup.number().when(FORMAT_OPTION, {
              is: val => val !== FORMAT_STORY,
              then: () => Yup.number().max(VIDEO_MAX_SIZE, 'The maximum supported video file size is 1 GB'),
              otherwise: () => Yup.number().max(STORY_VIDEO_MAX_SIZE, 'The maximum supported video file size is 250 MB')
            })
        })
    }),
    [MEDIA_WIDTH]: Yup.number().when([FORMAT_OPTION, IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY], {
      is: (FORMAT_OPTION, IS_CURRENT_MEDIA_CUSTOMIZED) =>
        FORMAT_OPTION === FORMAT_STORY || (FORMAT_OPTION === FORMAT_SINGLE && IS_CURRENT_MEDIA_CUSTOMIZED),
      then: () =>
        Yup.number().when(MEDIA_TYPE, {
          is: IMAGE,
          then: () => Yup.number().min(STORY_MEDIA_MIN_WIDTH, 'The minimum supported image width is 500px')
        }),
      otherwise: () =>
        Yup.number().when(MEDIA_TYPE, {
          is: IMAGE,
          then: () => Yup.number().min(IMAGE_MIN_WIDTH, 'The minimum supported image width is 600px'),
          otherwise: () =>
            Yup.number().when(MEDIA_TYPE, {
              is: VIDEO,
              then: () => Yup.number().min(VIDEO_MIN_WIDTH, 'The minimum supported video width is 120px')
            })
        })
    }),
    [MEDIA_HEIGHT]: Yup.number().when([FORMAT_OPTION, IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY], {
      is: (FORMAT_OPTION, IS_CURRENT_MEDIA_CUSTOMIZED) =>
        FORMAT_OPTION === FORMAT_STORY || (FORMAT_OPTION === FORMAT_SINGLE && IS_CURRENT_MEDIA_CUSTOMIZED),
      then: () =>
        Yup.number().when(MEDIA_TYPE, {
          is: IMAGE,
          then: () => Yup.number().min(IMAGE_MIN_HEIGHT, 'The minimum supported image height is 600px'),
          otherwise: () =>
            Yup.number().when(MEDIA_TYPE, {
              is: VIDEO,
              then: () => Yup.number().min(VIDEO_MIN_HEIGHT, 'The minimum supported video height is 120px')
            })
        })
    }),
    [MEDIA_RATIO]: Yup.number()
      .when(FORMAT_OPTION, {
        is: FORMAT_SINGLE,
        then: () => validateSingleMediaRatio
      })
      .when([FORMAT_OPTION, IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY], {
        is: (FORMAT_OPTION, IS_CURRENT_MEDIA_CUSTOMIZED) =>
          FORMAT_OPTION === FORMAT_STORY || (FORMAT_OPTION === FORMAT_SINGLE && IS_CURRENT_MEDIA_CUSTOMIZED),
        then: () => validateStoryMediaRatio
      })
      .when(FORMAT_OPTION, {
        is: FORMAT_CAROUSEL,
        then: () => validateCarouselMediaRatio
      }),
    [MEDIA_DURATION]: Yup.number().when(FORMAT_OPTION, {
      is: FORMAT_STORY,
      then: () =>
        Yup.number().when(MEDIA_TYPE, {
          is: VIDEO,
          then: () =>
            Yup.number()
              .min(VIDEO_MIN_DURATION, 'Video duration must be at least 1 second')
              .max(STORY_VIDEO_MAX_DURATION, 'Video duration should be less than 60 minutes')
        }),
      otherwise: () =>
        Yup.number().when(MEDIA_TYPE, {
          is: VIDEO,
          then: () =>
            Yup.number()
              .min(VIDEO_MIN_DURATION, 'Video duration must be at least 1 second')
              .max(VIDEO_MAX_DURATION, 'Video duration should be less than 241 minutes')
        })
    }),
    [CLICK_THROUGH_LINK]: Yup.string().when(FORMAT_OPTION, {
      is: FORMAT_SINGLE,
      then: () =>
        Yup.string()
          .test('link', 'Please enter a valid URL, including the https://', link => getUrlDomain(link))
          .max(1024, 'There is a maximum limit of 1024 characters')
          .required('This field should not be empty')
    }),
    // HEADLINE
    [AD_HEADLINE]: Yup.string().when(FORMAT_OPTION, {
      is: FORMAT_SINGLE,
      then: () =>
        Yup.string().when(HEADLINE_OPTION, {
          is: HEADLINE_ENTER,
          then: () =>
            Yup.string()
              .max(255, 'Headline should have maximum 255 characters')
              .required('This field should not be empty')
        })
    }),
    [CAROUSEL_ITEMS]: Yup.array().when(FORMAT_OPTION, {
      is: FORMAT_CAROUSEL,
      then: () => Yup.array().of(mediaItemSchema)
    }),
    [CAROUSEL_ITEMS_LENGTH]: Yup.number().when(FORMAT_OPTION, {
      is: FORMAT_CAROUSEL,
      then: () => Yup.number().min(2, 'You need at least two carousel items')
    }),
    [STORIES_ITEMS]: Yup.array().when(FORMAT_OPTION, {
      is: FORMAT_OPTION => FORMAT_OPTION === FORMAT_STORY,
      then: () =>
        Yup.array()
          .of(storiesItemShape)
          .test({
            name: 'Number of stories validation',
            test: (value, context) => {
              if (value.length === 2) {
                return context.createError({
                  message: 'Story creatives must have either 1 or 3 images/videos.',
                  path: STORIES_ITEMS
                })
              }

              return true
            }
          })
    }),
    [CUSTOMIZED_STORY_FILE]: Yup.object().when(CUSTOMIZED_MEDIA_OPTION, {
      is: UPLOAD_CUSTOMIZED_MEDIA,
      then: () =>
        Yup.object().shape({
          [FILE_URL]: Yup.string().required('Please select Image or Video file')
        }),
      otherwise: () => Yup.object().notRequired()
    }),
    ...(lineItem.objective === LEAD_GENERATION && {
      [LEAD_GEN_FORM]: Yup.string().required('Please select Instant Form')
    })
  })
}
